import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Building2, Hash, User, MapPin, CreditCard,
  QrCode, Phone, CheckCircle2, AlertCircle, Upload, X,
} from 'lucide-react';
import { companyBankAccountAPI } from '../../api/companyBankAccount';
import PageHeader from '../../components/ui/PageHeader';
import Button from '../../components/ui/Button';

// ── Toast ─────────────────────────────────────────────────────
function Toast({ toast }) {
  if (!toast) return null;
  const isSuccess = toast.type === 'success';
  return (
    <div className="fixed top-4 left-1/2 z-[300] w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 animate-fade-up">
      <div
        className="flex items-center gap-3 rounded-2xl px-4 py-3 shadow-xl"
        style={{
          background: isSuccess ? 'rgba(5,150,105,0.95)' : 'rgba(220,38,38,0.95)',
          border: `1px solid ${isSuccess ? 'rgba(110,231,183,0.3)' : 'rgba(252,165,165,0.3)'}`,
        }}
      >
        {isSuccess
          ? <CheckCircle2 size={16} color="#6ee7b7" />
          : <AlertCircle  size={16} color="#fca5a5" />}
        <p className="text-sm font-medium text-white">{toast.message}</p>
      </div>
    </div>
  );
}

// ── Field ─────────────────────────────────────────────────────
function Field({ label, icon: Icon, name, value, onChange, placeholder, type = 'text' }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="input-label flex items-center gap-1.5">
        <Icon size={12} style={{ color: '#d4a017' }} />
        {label}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="input-field"
      />
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────
const EMPTY = {
  bank_name:           '',
  account_holder_name: '',
  account_number:      '',
  ifsc:                '',
  branch:              '',
  upi_id:              '',
  upi_number:          '',
};

export default function CompanyBankAccountPage() {
  const [form,        setForm]        = useState(EMPTY);
  const [loading,     setLoading]     = useState(true);
  const [submitting,  setSubmitting]  = useState(false);
  const [toast,       setToast]       = useState(null);
  const [error,       setError]       = useState('');

  // QR code
  const [qrFile,      setQrFile]      = useState(null);       // new file to upload
  const [qrPreview,   setQrPreview]   = useState(null);       // local preview URL
  const [existingQr,  setExistingQr]  = useState(null);       // URL from server
  const fileInputRef                  = useRef(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchAccount = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await companyBankAccountAPI.get();
      if (data.status && data.data) {
        const d = data.data;
        setForm({
          bank_name:           d.bank_name           ?? '',
          account_holder_name: d.account_holder_name ?? '',
          account_number:      d.account_number      ?? '',
          ifsc:                d.ifsc                ?? '',
          branch:              d.branch              ?? '',
          upi_id:              d.upi_id              ?? '',
          upi_number:          d.upi_number          ?? '',
        });
        setExistingQr(d.qr_code_url ?? null);
      }
    } catch {
      setError('Failed to load company bank account details.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAccount(); }, [fetchAccount]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleQrChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setQrFile(file);
    setQrPreview(URL.createObjectURL(file));
  };

  const removeQr = () => {
    setQrFile(null);
    setQrPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, val]) => {
        formData.append(key, val);
      });
      if (qrFile) {
        formData.append('qr_code', qrFile);
      }

      const { data } = await companyBankAccountAPI.update(formData);
      if (data.status) {
        showToast('Company bank account saved successfully');
        // Refresh QR from server
        if (data.data?.qr_code_url) {
          setExistingQr(data.data.qr_code_url);
          setQrFile(null);
          setQrPreview(null);
        }
      } else {
        showToast(data.message || 'Failed to save details', 'error');
      }
    } catch (err) {
      showToast(
        err?.response?.data?.message || 'Something went wrong. Please try again.',
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const displayQr = qrPreview || existingQr;

  return (
    <>
      <Toast toast={toast} />

      <div className="flex flex-col min-h-0">
        <PageHeader
          title="Company Bank Account"
          subtitle="Manage bank and UPI payment details"
        />

        <div className="px-4 pb-8">

          {loading ? (
            <div className="flex flex-col gap-3 mt-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="skeleton h-14 rounded-2xl" />
              ))}
            </div>
          ) : error ? (
            <div
              className="mt-4 rounded-2xl px-4 py-4 text-sm text-center"
              style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', color: '#fca5a5' }}
            >
              {error}
              <button
                onClick={fetchAccount}
                className="block mx-auto mt-2 text-xs underline underline-offset-2"
                style={{ color: '#fca5a5' }}
              >
                Retry
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-2">

              {/* Bank Details */}
              <div
                className="rounded-2xl p-4 border flex flex-col gap-4"
                style={{ background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(212,160,23,0.15)' }}
              >
                <p className="text-xs font-semibold uppercase tracking-widest"
                   style={{ color: 'rgba(212,160,23,0.7)' }}>
                  Bank Details
                </p>
                <Field label="Bank Name"           icon={Building2} name="bank_name"           value={form.bank_name}           onChange={handleChange} placeholder="e.g. State Bank of India" />
                <Field label="Account Holder Name" icon={User}      name="account_holder_name" value={form.account_holder_name} onChange={handleChange} placeholder="e.g. Shine Jewellery Pvt Ltd" />
                <Field label="Account Number"      icon={Hash}      name="account_number"      value={form.account_number}      onChange={handleChange} placeholder="e.g. 1234567890" />
                <Field label="IFSC Code"           icon={CreditCard} name="ifsc"               value={form.ifsc}                onChange={handleChange} placeholder="e.g. SBIN0001234" />
                <Field label="Branch"              icon={MapPin}    name="branch"              value={form.branch}              onChange={handleChange} placeholder="e.g. MG Road, Bangalore" />
              </div>

              {/* UPI Details */}
              <div
                className="rounded-2xl p-4 border flex flex-col gap-4"
                style={{ background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(212,160,23,0.15)' }}
              >
                <p className="text-xs font-semibold uppercase tracking-widest"
                   style={{ color: 'rgba(212,160,23,0.7)' }}>
                  UPI Details
                </p>
                <Field label="UPI ID"     icon={CreditCard} name="upi_id"     value={form.upi_id}     onChange={handleChange} placeholder="e.g. shinejewellery@upi" />
                <Field label="UPI Number" icon={Phone}      name="upi_number" value={form.upi_number} onChange={handleChange} placeholder="e.g. 9876543210" type="tel" />
              </div>

              {/* QR Code */}
              <div
                className="rounded-2xl p-4 border flex flex-col gap-4"
                style={{ background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(212,160,23,0.15)' }}
              >
                <p className="text-xs font-semibold uppercase tracking-widest"
                   style={{ color: 'rgba(212,160,23,0.7)' }}>
                  UPI QR Code
                </p>

                {displayQr ? (
                  <div className="flex flex-col items-center gap-3">
                    <div className="relative">
                      <img
                        src={displayQr}
                        alt="UPI QR Code"
                        className="w-48 h-48 object-contain rounded-2xl"
                        style={{ border: '1px solid rgba(212,160,23,0.25)', background: 'rgba(255,255,255,0.05)' }}
                      />
                      {qrPreview && (
                        <button
                          type="button"
                          onClick={removeQr}
                          className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center"
                          style={{ background: 'rgba(220,38,38,0.9)', border: '1px solid rgba(252,165,165,0.3)' }}
                        >
                          <X size={12} color="white" />
                        </button>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="text-xs font-medium underline underline-offset-2"
                      style={{ color: '#f0c040' }}
                    >
                      {qrPreview ? 'Change QR Code' : 'Replace QR Code'}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex flex-col items-center gap-3 rounded-2xl py-8 transition-all active:scale-[0.98]"
                    style={{ border: '1.5px dashed rgba(212,160,23,0.35)', background: 'rgba(212,160,23,0.05)' }}
                  >
                    <div
                      className="w-12 h-12 rounded-2xl flex items-center justify-center"
                      style={{ background: 'rgba(212,160,23,0.12)' }}
                    >
                      <QrCode size={22} style={{ color: '#d4a017' }} />
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-semibold text-white flex items-center gap-1.5 justify-center">
                        <Upload size={13} /> Upload QR Code
                      </p>
                      <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
                        JPG or PNG · max 15 MB
                      </p>
                    </div>
                  </button>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png"
                  className="hidden"
                  onChange={handleQrChange}
                />
              </div>

              <Button type="submit" fullWidth loading={submitting}>
                Save Details
              </Button>

            </form>
          )}
        </div>
      </div>
    </>
  );
}
